import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ProductCacheService } from 'src/app/services/cache-service/product-cache.service';
import { ProductSearchResultDto } from './models/product-search';
import { ValueTextPair } from 'src/app/shared/models/value-text-pair';

@Component({
  selector: 'app-stocks-product-search',
  templateUrl: './product-search.component.html',
  styleUrls: ['./product-search.component.css']
})
export class ProductSearchComponent implements OnInit, OnDestroy {

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput', { read: MatAutocompleteTrigger }) autoComplete: MatAutocompleteTrigger;

  /** 輸入框 placeholder */
  @Input() placeholder: string = '輸入貨號、商品名稱、廠商自編碼、條碼、醫令代碼、健保碼';

  /** 是否禁用 */
  @Input() disabled: boolean = false;

  /** 輸入框的 id (用於外部 label for 連結) */
  @Input() inputId: string = '';

  /** 輸入框樣式類別 */
  @Input() inputClass: string = 'vis-grid-input';

  /** 排除的商品 ID 清單 */
  @Input() excludeProductIds: number[] = [];

  /** 商品類型篩選 (1=一般商品, 2=組合商品, null=不篩選) */
  @Input() productType: number = null;

  /** 庫存類別篩選 (1=一般商品, 2=母商品, null=不篩選) */
  @Input() stockType: number = null;

  /** 是否只搜尋計算庫存的商品 (true=只搜尋庫存商品, false/null=不篩選) */
  @Input() isInventory: boolean = null;

  /** 是否只搜尋使用批號的商品 (true=只搜尋使用批號商品, false/null=不篩選) */
  @Input() isUseBatchNo: boolean = null;

  /** 進貨稅別篩選 (0=免稅, 1=應稅, null=不篩選) */
  @Input() stockTaxType: number = null;

  /** 銷售稅別篩選 (0=免稅, 1=應稅, null=不篩選) */
  @Input() saleTaxType: number = null;

  /** 廠商代碼篩選 (null=不篩選) */
  @Input() vendorCode: string = null;

  /** 排除停止退貨的商品 (true=排除, null=不篩選) */
  @Input() excludeStopReturn: boolean = null;

  /** 排除停止進貨的商品 (true=排除, null=不篩選) */
  @Input() excludeStopStock: boolean = null;

  /** 排除停止銷售的商品 (true=排除, null=不篩選) */
  @Input() excludeStopSale: boolean = null;

  /** 最多顯示幾筆 */
  @Input() top: number = 100;

  /** 目前顯示的值 */
  @Input() displayValue: string = '';

  /** 選擇商品時觸發 */
  @Output() productSelected = new EventEmitter<ProductSearchResultDto>();

  /** 清空輸入時觸發 */
  @Output() inputCleared = new EventEmitter<void>();

  /** Tab 鍵按下時觸發（用於 Tab 導航） */
  @Output() tabPressed = new EventEmitter<KeyboardEvent>();

  /** 搜尋選項 */
  productOptions: ValueTextPair[] = [];

  /** 是否正在載入快取 */
  isLoading: boolean = false;

  /** 是否顯示無結果提示 */
  showNoResult: boolean = false;

  /** 目前搜尋的關鍵字 (用於Highlight顯示) */
  currentKeyword: string = '';

  private searchKeyword$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private productCache: ProductCacheService
  ) {}

  async ngOnInit() {
    // 載入快取
    if (!this.productCache.isCacheLoaded()) {
      this.isLoading = true;
      await this.productCache.loadCache();
      this.isLoading = false;
    }

    // 設定搜尋防抖動
    this.searchKeyword$.pipe(
      debounceTime(50),
      takeUntil(this.destroy$)
    ).subscribe(keyword => {
      this.doSearch(keyword);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** 鍵盤按下 */
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.productOptions = [];
      return;
    }

    // Tab 鍵處理
    if (event.key === 'Tab') {
      // 如果有選項，選取第一筆
      if (this.productOptions.length > 0) {
        const firstOption = this.productOptions[0];
        if (firstOption?.extension) {
          const product = firstOption.extension as ProductSearchResultDto;
          this.productSelected.emit(product);
          if (this.searchInput) {
            this.searchInput.nativeElement.value = product.ProductCode;
          }
          this.productOptions = [];
        }
      }

      this.tabPressed.emit(event);
    }
  }

  /** 鍵盤放開 (觸發搜尋) */
  onKeyUp(event: KeyboardEvent) {
    const ignoredKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape', 'Tab'];
    if (ignoredKeys.includes(event.key)) {
      return;
    }

    const keyword = (event.target as HTMLInputElement).value;

    // 輸入框被清空
    if (!keyword || keyword.trim() === '') {
      this.inputCleared.emit();
    }

    this.searchKeyword$.next(keyword);
  }

  /** 執行搜尋 */
  private doSearch(keyword: string) {
    if (!keyword || keyword.length < 1) {
      this.productOptions = [];
      this.showNoResult = false;
      this.currentKeyword = '';
      return;
    }

    // 保存關鍵字供Highlight使用
    this.currentKeyword = keyword.trim().toUpperCase();

    // 使用快取服務進行搜尋
    const products = this.productCache.search(keyword, {
      excludeProductIds: this.excludeProductIds,
      productType: this.productType,
      stockType: this.stockType,
      isInventory: this.isInventory,
      isUseBatchNo: this.isUseBatchNo,
      stockTaxType: this.stockTaxType,
      saleTaxType: this.saleTaxType,
      vendorCode: this.vendorCode,
      excludeStopReturn: this.excludeStopReturn,
      excludeStopStock: this.excludeStopStock,
      excludeStopSale: this.excludeStopSale,
      top: this.top
    });

    // 使用索引作為 value，確保每筆展開記錄有唯一識別
    this.productOptions = products.map((p, index) => ({
      value: index.toString(),
      text: `${p.ProductCode} - ${p.ProductName}`,
      extension: p
    }));

    this.showNoResult = products.length === 0;
  }

  /** 選擇商品 (mat-autocomplete 事件) */
  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const selectedValue = event.option.value;
    const selectedOption = this.productOptions.find(p => p.value === selectedValue);

    if (selectedOption && selectedOption.extension) {
      const product = selectedOption.extension as ProductSearchResultDto;
      this.productSelected.emit(product);

      // 更新輸入框顯示
      if (this.searchInput) {
        this.searchInput.nativeElement.value = product.ProductCode;
      }
    }

    this.productOptions = [];
  }

  /** 設定顯示值 */
  setValue(value: string) {
    if (this.searchInput) {
      this.searchInput.nativeElement.value = value;
    }
  }

  /** 清空 */
  clear() {
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    this.productOptions = [];
  }

  /** 重新載入快取 */
  async refreshCache() {
    this.isLoading = true;
    await this.productCache.refreshCache();
    this.isLoading = false;
  }

  /** 取得文字Highlight資訊 */
  getHighlight(text: string): { before: string; match: string; after: string } {
    if (!text || !this.currentKeyword) {
      return { before: text || '', match: '', after: '' };
    }

    const upperText = text.toUpperCase();
    const index = upperText.indexOf(this.currentKeyword);

    if (index === -1) {
      return { before: text, match: '', after: '' };
    }

    return {
      before: text.substring(0, index),
      match: text.substring(index, index + this.currentKeyword.length),
      after: text.substring(index + this.currentKeyword.length)
    };
  }
}
